import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { SpgOption, SpgEngine, SpgModel } from 'src/models';
import * as acBase from 'src/store/states/GenericActionCreator';
import { IStoreState } from 'src/store';

// Cache-key builders shared with PropertyOptions.tsx/MobileView.tsx's stale-data guards
// single source of truth for the key format rather than re-deriving inline.
export const buildSpgModelsKey = (make: string, model: string, year: string) => `${make}|${model}|${year}`;
export const buildSpgOptionsKey = (make: string, model: string, year: string, spgModelId: string) => `${buildSpgModelsKey(make, model, year)}|${spgModelId}`;
export const buildSpgEnginesKey = (make: string, model: string, year: string, spgModelId: string, optionId: string) => `${buildSpgOptionsKey(make, model, year, spgModelId)}|${optionId}`;

export interface ISpgVehicleState {
    options: SpgOption[];
    engines: SpgEngine[];
    spgModels: SpgModel[];
    optionsBuilt: boolean;
    enginesBuilt: boolean;
    spgModelsBuilt: boolean;
    optionsKey: string;
    enginesKey: string;
    spgModelsKey: string;
}

export const initialState: ISpgVehicleState = {
    options: [],
    engines: [],
    spgModels: [],
    optionsBuilt: false,
    enginesBuilt: false,
    spgModelsBuilt: false,
    optionsKey: '',
    enginesKey: '',
    spgModelsKey: '',
};

export const SpgVehicleSlice = createSlice({
    name: 'SpgVehicle',
    initialState: initialState as ISpgVehicleState,
    reducers: {
        setOptions: (state, action: PayloadAction<SpgOption[]>) => {
            state.options = action.payload;
        },
        setEngines: (state, action: PayloadAction<SpgEngine[]>) => {
            state.engines = action.payload;
        },
        setOptionsBuilt: (state, action: PayloadAction<boolean>) => {
            state.optionsBuilt = action.payload;
        },
        setEnginesBuilt: (state, action: PayloadAction<boolean>) => {
            state.enginesBuilt = action.payload;
        },
        setOptionsKey: (state, action: PayloadAction<string>) => {
            state.optionsKey = action.payload;
        },
        setEnginesKey: (state, action: PayloadAction<string>) => {
            state.enginesKey = action.payload;
        },
        setSpgModels: (state, action: PayloadAction<SpgModel[]>) => {
            state.spgModels = action.payload;
        },
        setSpgModelsBuilt: (state, action: PayloadAction<boolean>) => {
            state.spgModelsBuilt = action.payload;
        },
        setSpgModelsKey: (state, action: PayloadAction<string>) => {
            state.spgModelsKey = action.payload;
        },
        clearConfig: (state) => {
            state.options = [];
            state.engines = [];
            state.optionsBuilt = false;
            state.enginesBuilt = false;
            state.optionsKey = '';
            state.enginesKey = '';
            state.spgModels = [];
            state.spgModelsBuilt = false;
            state.spgModelsKey = '';
        },
    },
});

export const {
    setOptions,
    setEngines,
    setOptionsBuilt,
    setEnginesBuilt,
    setOptionsKey,
    setEnginesKey,
    setSpgModels,
    setSpgModelsBuilt,
    setSpgModelsKey,
    clearConfig,
} = SpgVehicleSlice.actions;

export default SpgVehicleSlice.reducer;

interface OptResponse { returnCode?: string; options?: SpgOption[]; }
interface EngResponse { returnCode?: string; engines?: SpgEngine[]; }
interface MdlResponse { returnCode?: string; models?: SpgModel[]; }

/**
 * Fetches SPG drivetrain options for a no-VIN vehicle. Sets optionsKey to
 * `make|model|year|spgModelId` so consumers can detect stale data left over from a previously
 * selected vehicle.
 */
export function GetSpgOptions(make: string, model: string, year: string, spgModelId: string) {
    return async (dispatch, getState: () => IStoreState) => {
        const store = getState().Framework.store;
        const branch = getState().Framework.branch;
        const optionsKey = buildSpgOptionsKey(make, model, year, spgModelId);
        const reqData = {
            functionFlag: 'OPT',
            make: make,
            model: model,
            year: year,
            spgModelId: spgModelId,
            logId: getState().Framework.logId,
        };
        const response = await acBase.eraWebServiceCall<OptResponse | AxiosError>(
            'SVPController', 'GetSpecs', reqData, store, branch);
        if (acBase.handleAxiosError(response, dispatch, { operation: 'GetSpgOptions' })) {
            return;
        }
        dispatch(setOptions(response?.options ?? []));
        dispatch(setOptionsKey(optionsKey));
        dispatch(setOptionsBuilt(true));
    };
}

/**
 * Fetches SPG engines for a no-VIN vehicle's selected option. Sets enginesKey to
 * `make|model|year|spgModelId|optionId` so consumers can detect stale data left over from a
 * previously selected vehicle/option.
 */
export function GetSpgEngines(
    make: string, model: string, year: string, spgModelId: string, optionId: string,
) {
    return async (dispatch, getState: () => IStoreState) => {
        const store = getState().Framework.store;
        const branch = getState().Framework.branch;
        const reqData = {
            functionFlag: 'ENG',
            make: make,
            model: model,
            year: year,
            spgModelId: spgModelId,
            optionId: optionId,
            logId: getState().Framework.logId,
        };
        const enginesKey = buildSpgEnginesKey(make, model, year, spgModelId, optionId);
        const response = await acBase.eraWebServiceCall<EngResponse | AxiosError>(
            'SVPController', 'GetSpecs', reqData, store, branch);
        if (acBase.handleAxiosError(response, dispatch, { operation: 'GetSpgEngines' })) {
            return;
        }
        dispatch(setEngines(response?.engines ?? []));
        dispatch(setEnginesKey(enginesKey));
        dispatch(setEnginesBuilt(true));
    };
}

/**
 * Fetches the SPG models a Chrome model fuzzy-matches to for a no-VIN vehicle. Sets
 * spgModelsKey to `make|model|year` so consumers can detect stale data left over from a
 * previously selected vehicle.
 */
export function GetSpgModels(make: string, model: string, year: string) {
    return async (dispatch, getState: () => IStoreState) => {
        const store = getState().Framework.store;
        const branch = getState().Framework.branch;
        const spgModelsKey = buildSpgModelsKey(make, model, year);
        const reqData = {
            functionFlag: 'MDL',
            make: make,
            model: model,
            year: year,
            logId: getState().Framework.logId,
        };
        const response = await acBase.eraWebServiceCall<MdlResponse | AxiosError>(
            'SVPController', 'GetSpecs', reqData, store, branch);
        if (acBase.handleAxiosError(response, dispatch, { operation: 'GetSpgModels' })) {
            return;
        }
        dispatch(setSpgModels(response?.models ?? []));
        dispatch(setSpgModelsKey(spgModelsKey));
        dispatch(setSpgModelsBuilt(true));
    };
}

/**
 * Clears all SPG options/engines/models config (and their built/key flags) back to initial
 * state. Called when the make/model changes or the no-VIN SPG flow becomes disabled, so stale
 * data from a previously selected vehicle can't leak into the new one.
 */
export function ClearSpgVehicle() {
    return (dispatch) => {
        dispatch(clearConfig());
    };
}
